import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Autocomplete,
  Avatar,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../../Redux/Reducers/uiSlice";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import {
  investArr,
  hashInvestment,
} from "../../../component/Methods";
import { sendWebSocketMessage } from "../../../hooks/useWebSocket";
import { useTradeBlotterState } from "../../../hooks/useOptimizedState";
import {
  getSmaBucketStrategyLabel,
  isFundSma,
  buildTickerDropdownOptions,
  TEMP_TICKER_DROPDOWN_ROWS,
} from "../utility";
import {
  getActionAndPosition,
  getTradeActionOptions,
  sortFundsByAbbreviation,
  sortBrokersByAbbreviation,
  sortStrategiesForOrderDialog,
  actionFromActionPosition,
  getFundAccountNames,
  applyAssetTypeToForm,
  getInvestmentTypeFromAssetType,
  validateOptionFormFields,
  buildOptionFieldsForOrder,
  buildOrderTickerPayload,
  buildDistributionKey,
  resolveRoutingEnum,
  resolveEditRoutingDisplay,
  validateNonSmaStrategy,
} from "./orderDialogHelpers";
import { useSmaBucketStrategySync } from "../hooks/useSmaBucketStrategySync";
import TickerSymbolAutocomplete from "../components/TickerSymbolAutocomplete";
import OrderOptionFieldsSection from "../components/OrderOptionFieldsSection";

const EditTradeDialog = ({ open, onClose, tradeData }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  // Redux selectors
  const { funds, strategies, brokers, lots, managers } = useTradeBlotterState();
  const user = useSelector((state) => state.auth?.user?.email);

  // Form state
  const [formData, setFormData] = useState({
    fund: "",
    strategy: "",
    action: "",
    ticker: "",
    stock: "",
    option: 1, // Options enum: calls=0, puts=1 (default puts)
    strikePrice: "",
    expirationDate: "", // YYYY-MM-DD
    broker: "",
    account: "",
    shareCount: "",
    routing: "",
    assetType: "Equity",
  });

  const sortedFunds = useMemo(() => sortFundsByAbbreviation(funds), [funds]);

  const sortedBrokers = useMemo(() => sortBrokersByAbbreviation(brokers), [brokers]);

  // Initialize form with trade data when dialog opens
  useEffect(() => {
    if (tradeData && open) {
      // Get asset type from trade data's investment type
      const assetTypeName =
        tradeData.investment !== undefined
          ? hashInvestment(tradeData.investment) || "Equity"
          : "Equity";

      const initialAction = actionFromActionPosition(
        tradeData?.action,
        tradeData?.position
      );

      const existingOption = tradeData?.lastOrder?.ticker?.option;
      const existingStrikePrice = tradeData?.lastOrder?.ticker?.strikePrice;
      const existingExpirationDate = tradeData?.lastOrder?.ticker?.expirationDate;
      const expirationDateYmd = existingExpirationDate
        ? new Date(existingExpirationDate).toISOString().slice(0, 10)
        : "";

      const orderTicker = tradeData?.lastOrder?.ticker;
      setFormData({
        fund: tradeData.fundAbbreviation || "",
        strategy: tradeData.strategy || "",
        action: initialAction,
        ticker: orderTicker?.symbol?.toUpperCase() || "",
        stock:
          orderTicker?.stock != null && String(orderTicker.stock).trim() !== ""
            ? String(orderTicker.stock)
            : "",
        option: existingOption === 0 ? 0 : 1,
        strikePrice:
          existingStrikePrice !== undefined && existingStrikePrice !== null
            ? String(existingStrikePrice)
            : "",
        expirationDate: expirationDateYmd,
        broker: tradeData.brokerAbbreviation || "",
        account: tradeData.account || "",
        shareCount: tradeData.quantity || "",
        routing: tradeData.routing !== undefined ? tradeData.routing : "Manual",
        assetType: assetTypeName,
      });
    }
  }, [tradeData, open, sortedBrokers]);

  const sortedStrategies = useMemo(
    () => sortStrategiesForOrderDialog(strategies),
    [strategies]
  );
  const selectedFundMeta = useMemo(
    () => funds?.find((f) => f.abbreviation === formData.fund),
    [funds, formData.fund]
  );
  const isSmaSelectedFund = isFundSma(selectedFundMeta?.fundId, managers);
  const smaBucketLabel = useMemo(
    () => getSmaBucketStrategyLabel(strategies),
    [strategies]
  );

  useSmaBucketStrategySync({
    formData,
    setFormData,
    selectedFundMeta,
    managers,
    strategies,
  });

  const sortedAccounts = useMemo(
    () => getFundAccountNames(funds, formData.fund),
    [formData.fund, funds]
  );

  const uniqueTickers = useMemo(
    () =>
      buildTickerDropdownOptions({
        lots,
        orderTicker: tradeData?.lastOrder?.ticker,
        extraRows: TEMP_TICKER_DROPDOWN_ROWS,
      }),
    [lots, tradeData]
  );

  const investmentTypeForUi = getInvestmentTypeFromAssetType(formData.assetType);
  const showOptionFields = investmentTypeForUi === 10;
  const actionOptions = getTradeActionOptions(showOptionFields);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      if (field === "assetType") {
        return applyAssetTypeToForm(prev, value || "Equity");
      }
      return {
        ...prev,
        [field]: value,
      };
    });

    // Reset account when fund changes; clear strategy for non-SMA so it stays empty until chosen.
    if (field === "fund") {
      const newFund = funds?.find((f) => f.abbreviation === value);
      const nextSma = isFundSma(newFund?.fundId, managers);
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        account: "",
        ...(!nextSma ? { strategy: "" } : {}),
      }));
    }
  };

  const handleSubmit = () => {
    // Validate required fields
    if (
      !formData.fund ||
      !formData.action ||
      !formData.ticker ||
      !formData.broker ||
      !formData.account ||
      !formData.shareCount ||
      !formData.routing
    ) {
      alert("Please fill in all required fields");
      return;
    }

    // Get broker ID
    const broker = brokers?.find((b) => b.abbreviation === formData.broker);
    if (!broker?.brokerId) {
      alert("Invalid broker selected");
      return;
    }

    const { action: actionValue, position } = getActionAndPosition(
      formData.action
    );

    const selectedFund = funds?.find(
      (fund) => fund.abbreviation === formData.fund
    );
    if (!selectedFund?.fundId) {
      alert("Invalid fund selected");
      return;
    }

    const isSmaFund = isFundSma(selectedFund.fundId, managers);
    const strategyResult = validateNonSmaStrategy(
      formData,
      isSmaFund,
      strategies
    );
    if (!strategyResult.ok) return;

    const distributionKey = buildDistributionKey({
      account: formData.account,
      fundId: selectedFund.fundId,
      isSmaFund,
      strategyRecord: strategyResult.strategy,
    });
    const distribution = {};
    distribution[distributionKey] = parseInt(formData.shareCount, 10);

    const investmentType = getInvestmentTypeFromAssetType(formData.assetType);
    if (!validateOptionFormFields(formData, investmentType)) return;

    const optionFields = buildOptionFieldsForOrder(formData, investmentType);

    const orderData = {
      distribution,
      action: actionValue,
      position: position,
      broker: broker.brokerId,
      type: 1,
      typePrice: 0,
      instructions: "best way",
      routing: resolveRoutingEnum(formData.routing),
      ticker: buildOrderTickerPayload(formData, investmentType, optionFields),
      user: user,
    };

    // Create request data
    const requestData = {
      action: "modify_orders",
      data: [
        {
          order: orderData,
          tradeid: tradeData.tradeId,
        },
      ],
    };

    // Show loader and add to active requests
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Updating trade order..."));
    dispatch(addActiveRequests("modify_orders"));

    sendWebSocketMessage(JSON.stringify(requestData));
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 48,
              height: 48,
            }}
          >
            <EditIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              Edit Order
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Modify existing trading order
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2, overflowY: "visible" }}>
        <Grid container spacing={1.5}>
          {/* Fund Selection */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={sortedFunds}
              getOptionLabel={(option) => option.abbreviation || ""}
              value={
                sortedFunds.find(
                  (fund) => fund.abbreviation === formData.fund
                ) || null
              }
              onChange={(event, newValue) =>
                handleInputChange("fund", newValue?.abbreviation || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fund"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>

          {/* Strategy: required for non-SMA; SMA shows fixed CPFactorBW bucket (read-only). */}
          {(sortedStrategies.length > 0 || isSmaSelectedFund) && (
            <Grid size={{ xs: 12 }}>
              {isSmaSelectedFund ? (
                <TextField
                  fullWidth
                  label="Strategy"
                  value={smaBucketLabel}
                  disabled
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <Autocomplete
                  options={sortedStrategies}
                  getOptionLabel={(option) =>
                    option.name || option.strategyId || ""
                  }
                  value={
                    sortedStrategies?.find(
                      (strategy) =>
                        strategy.name === formData.strategy ||
                        strategy.strategyId === formData.strategy ||
                        strategy.id === formData.strategy
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    handleInputChange(
                      "strategy",
                      newValue?.name ||
                        newValue?.strategyId ||
                        newValue?.id ||
                        ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Strategy"
                      variant="outlined"
                      size="small"
                      required
                    />
                  )}
                  disableClearable
                />
              )}
            </Grid>
          )}
          {/* Asset Type */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={investArr}
              value={formData.assetType}
              onChange={(event, newValue) =>
                handleInputChange("assetType", newValue || "Equity")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset Type"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>
          {/* Action */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={actionOptions}
              getOptionLabel={(option) => option.label}
              value={
                actionOptions.find(
                  (action) => action.value === formData.action
                ) || null
              }
              onChange={(event, newValue) =>
                handleInputChange("action", newValue?.value || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Action"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TickerSymbolAutocomplete
              uniqueTickers={uniqueTickers}
              ticker={formData.ticker}
              stock={formData.stock}
              setFormData={setFormData}
              placeholder="e.g., AAPL, MSFT"
              inputLabelProps={{ shrink: true }}
            />
          </Grid>

          {showOptionFields && (
            <OrderOptionFieldsSection
              formData={formData}
              onFieldChange={handleInputChange}
              inputLabelProps={{ shrink: true }}
            />
          )}

          {/* Broker */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={sortedBrokers}
              getOptionLabel={(option) => option.abbreviation || ""}
              value={
                sortedBrokers.find(
                  (broker) => broker.abbreviation === formData.broker
                ) || null
              }
              onChange={(event, newValue) =>
                handleInputChange("broker", newValue?.abbreviation || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Broker"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>
          {/* Routing */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={["Manual", "Auto"]}
              value={resolveEditRoutingDisplay(formData.routing)}
              onChange={(event, newValue) =>
                handleInputChange("routing", newValue || "Manual")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Routing"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>

          {/* Account and Share Count Row */}
          <Grid size={{ xs: 6 }}>
            <Autocomplete
              options={sortedAccounts}
              value={formData.account || ""}
              onChange={(event, newValue) =>
                handleInputChange("account", newValue || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Account"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Share Count"
              value={formData.shareCount}
              onChange={(e) => handleInputChange("shareCount", e.target.value)}
              variant="outlined"
              size="small"
              type="number"
              required
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, justifyContent: "flex-end" }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            minWidth: 120,
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Update Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTradeDialog;
